import * as React from "react";
import * as SharedState from "../shared-state";
import * as Authorization from "@genesys/shared/lib/authorization";
import * as LanguageTexts from "@genesys/shared/lib/language-texts";
import { State, Action } from "./state";
import { Dispatch } from "@typescript-tea/core";
import { OverlayLoader, P1 } from "@genesys/ui-elements";
import { Root } from "./elements";
import { MainContentView, TopContentView } from "./components";
import { ModalComponent } from "../shared-manager-components";
import { exhaustiveCheck } from "ts-exhaustive-check";

export function PricingManagerView({
  state,
  sharedState,
  dispatch
}: {
  readonly dispatch: Dispatch<Action>;
  readonly state: State;
  readonly sharedState: SharedState.State;
}): JSX.Element {
  const canUsePricing = Authorization.checkPermission(
    sharedState.user.applicationClaims,
    Authorization.genesysPricingClaims.canUsePricing
  );

  const canSeePricing = Authorization.checkPermission(
    sharedState.user.applicationClaims,
    Authorization.genesysPricingClaims.canSeePricing
  );

  if (!canUsePricing && !canSeePricing) {
    return <Root>Missing Claims</Root>;
  }

  if (!state.productQueryResult) {
    return <OverlayLoader />;
  }

  return (
    <Root>
      <Modal state={state} sharedState={sharedState} dispatch={dispatch} />
      <TopContentView
        dispatch={dispatch}
        sharedState={sharedState}
        state={state}
      />
      <MainContentView
        dispatch={dispatch}
        sharedState={sharedState}
        state={state}
      />
    </Root>
  );
}

function Modal({
  dispatch,
  state,
  sharedState
}: {
  readonly dispatch: Dispatch<Action>;
  readonly state: State;
  readonly sharedState: SharedState.State;
}): JSX.Element | null | undefined {
  if (!state.modal) {
    return null;
  }

  const mode = state.modal.mode;

  switch (mode) {
    case "confirm-label-delete": {
      const modal = state.modal;
      if (!modal.label) {
        return null;
      }
      return (
        <ModalComponent
          sharedState={sharedState}
          header={sharedState.translate(LanguageTexts.confirmation())}
          content={sharedState.translate(
            LanguageTexts.deleteLabelMsg(modal.label.name)
          )}
          onClose={() => dispatch(Action.closeModal())}
          onConfirm={() => {
            dispatch(Action.deleteLabel(modal.label?.id!));
            dispatch(Action.closeModal());
          }}
        />
      );
    }
    case "must-check-all-revisions": {
      return (
        <ModalComponent
          sharedState={sharedState}
          header={sharedState.translate(LanguageTexts.error())}
          content={sharedState.translate(
            LanguageTexts.dynamicText("must_check_all_revisions")
          )}
          onClose={() => dispatch(Action.closeModal())}
        />
      );
    }
    case "confirm-pricing-delete": {
      const pricingFieldIds = state.modal.pricingFieldIds || [];
      return (
        <ModalComponent
          sharedState={sharedState}
          header={sharedState.translate(LanguageTexts.confirmation())}
          content={sharedState.translate(
            LanguageTexts.deleteAllSelectedMsg(state.selectedPricingIds.length)
          )}
          onClose={() => dispatch(Action.closeModal())}
          onConfirm={() => dispatch(Action.deletePricings(pricingFieldIds))}
        />
      );
    }
    case "help-about-searching": {
      return (
        <ModalComponent
          sharedState={sharedState}
          header={sharedState.translate(LanguageTexts.helpmenu())}
          content={
            <>
              <P1 weight="bold">Search logic:</P1>
              <P1 weight="normal">
                One or more search conditions of any type (see syntax) can be
                used. Search conditions are combined and all supplied conditions
                must be fulfilled to get a hit.
              </P1>
              <P1 weight="bold">Search syntax:</P1>
              <P1 weight="normal">
                label:xxx - Systems with specified label attached gn:G00000x -
                System with specified Genesys number owner:XxxXxx - Systems
                created by sepecified login name type:XXX - Systems of specified
                system type xxxxxxx - String without prefix searches in file
                name, system revision comments and labels, genesys number but
                can be significantly slower
              </P1>
              <P1 weight="bold">Examples: </P1>
              <P1 weight="normal">
                'label:test label:project1 type:mlp' - Searches all systems with
                label 'test' AND label 'project1' that have system type 'MLP'
              </P1>
            </>
          }
          onClose={() => dispatch(Action.closeModal())}
        />
      );
    }
    default:
      exhaustiveCheck(mode, true);
  }
}
